//
//  components/pwa/AddToHomeScreenPrompt.tsx
//  TownLIT
//
//  Created by Hossein Sakkaki on 2026-08-19.
//  Last Update by Hossein Sakkaki on 2026-08-19.
//

"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import {
  shouldOfferTownLITPWA,
  TOWNLIT_PWA_PROMPT,
  TOWNLIT_ROUTES,
} from "../../config/townlitDistribution";

import { useClientPlatform } from "../../hooks/platform/useClientPlatform";
import { usePWAInstall } from "../../hooks/pwa/usePWAInstall";

import Button from "../common/Button/Button";

import "./AddToHomeScreenPrompt.css";

function hasRecentDismissal(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const rawValue = localStorage.getItem(
      TOWNLIT_PWA_PROMPT.dismissStorageKey,
    );

    if (!rawValue) {
      return false;
    }

    const dismissedAt = Number(rawValue);

    if (!Number.isFinite(dismissedAt)) {
      localStorage.removeItem(
        TOWNLIT_PWA_PROMPT.dismissStorageKey,
      );

      return false;
    }

    const elapsed = Date.now() - dismissedAt;

    if (elapsed >= TOWNLIT_PWA_PROMPT.dismissCooldownMs) {
      localStorage.removeItem(
        TOWNLIT_PWA_PROMPT.dismissStorageKey,
      );

      return false;
    }

    return true;
  } catch {
    return false;
  }
}

function persistDismissal(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(
      TOWNLIT_PWA_PROMPT.dismissStorageKey,
      String(Date.now()),
    );
  } catch {
    // Ignore unavailable storage.
  }
}

function clearDismissal(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(
      TOWNLIT_PWA_PROMPT.dismissStorageKey,
    );
  } catch {
    // Ignore unavailable storage.
  }
}

export default function AddToHomeScreenPrompt() {
  const pathname = usePathname();
  const platform = useClientPlatform();

  const {
    canInstall,
    alreadyInstalled,
    install,
  } = usePWAInstall();

  const [isVisible, setIsVisible] = useState(false);

  const canOfferPWA = shouldOfferTownLITPWA(platform);

  // The dedicated app page already has its own install action.
  const isAppDistributionPage =
    pathname === TOWNLIT_ROUTES.app;

  useEffect(() => {
    if (
      !canOfferPWA ||
      !canInstall ||
      alreadyInstalled ||
      isAppDistributionPage
    ) {
      setIsVisible(false);
      return;
    }

    if (hasRecentDismissal()) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
  }, [
    canOfferPWA,
    canInstall,
    alreadyInstalled,
    isAppDistributionPage,
  ]);

  const handleHide = () => {
    persistDismissal();
    setIsVisible(false);
  };

  const handleInstall = async () => {
    const result = await install();

    if (result === "accepted") {
      clearDismissal();
    } else {
      persistDismissal();
    }

    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="a2hs-container"
      role="dialog"
      aria-modal="false"
      aria-label="Install TownLIT"
    >
      <div className="a2hs-card">
        <div className="a2hs-header">
          <img
            src="/images/logo/TownLITLogo.png"
            alt="TownLIT"
            className="a2hs-logo"
          />

          <div className="a2hs-title">
            <h3>Install TownLIT</h3>

            <p className="a2hs-sub">
              Add TownLIT to your Android device for quick access
              from your home screen.
            </p>
          </div>
        </div>

        <div className="a2hs-description">
          <p className="util-text-hint">
            TownLIT is available as a web app on Android while the
            native Android app is being prepared.
          </p>
        </div>

        <div className="a2hs-actions">
          <Button
            variant="outline"
            size="md"
            align="center"
            onClick={handleHide}
            className="a2hs-btn"
          >
            Maybe later
          </Button>

          <Button
            variant="primary"
            size="md"
            align="center"
            onClick={handleInstall}
            className="a2hs-btn"
          >
            Install TownLIT
          </Button>
        </div>
      </div>
    </div>
  );
}